import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminClients = ({ token, clients, onRefresh, onNavigate, navigationParams }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({});

  useEffect(() => {
    if (navigationParams?.filterClientId) {
      const client = clients?.find(c => c.id === navigationParams.filterClientId);
      if (client) setSearchTerm(client.full_name || client.email || '');
    }
  }, [navigationParams, clients]);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const openEditClientModal = (client) => {
    setEditingClient(client);
    setEditClientForm({
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      phone_country_code: client.phone_country_code || '+58',
      id_document: client.id_document || '',
      customer_type: client.customer_type || 'publico',
      delivery_address: client.delivery_address || '',
      delivery_city: client.delivery_city || '',
      delivery_state: client.delivery_state || '',
      is_active: client.is_active !== false
    });
  };

  const handleSaveClientEdit = async () => {
    if (!editingClient) return;
    try {
      await axios.put(`${API}/admin/clients/${editingClient.id}`, editClientForm, axiosConfig);
      alert('Cliente actualizado exitosamente');
      setEditingClient(null);
      onRefresh();
    } catch (error) {
      alert('Error al actualizar cliente: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBatchDeleteClients = async () => {
    if (selectedClients.length === 0) { alert('Seleccione al menos un cliente para eliminar'); return; }
    if (!window.confirm(`Esta seguro de eliminar ${selectedClients.length} cliente(s)? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.post(`${API}/admin/clients/batch-delete`, { client_ids: selectedClients }, axiosConfig);
      alert(`${selectedClients.length} cliente(s) eliminado(s) exitosamente`);
      setSelectedClients([]);
      onRefresh();
    } catch (error) {
      alert('Error al eliminar clientes: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredClients = clients.filter(client =>
    client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.locker_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Clientes Registrados ({clients.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar por nombre, email o casillero..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="client-search-input" />
            </div>
          </div>

          {selectedClients.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <span className="text-blue-700 font-medium">{selectedClients.length} cliente(s) seleccionado(s)</span>
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" onClick={handleBatchDeleteClients}>Eliminar Seleccionados</Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedClients([])}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-3 w-10">
                    <input type="checkbox" checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                      onChange={(e) => { e.target.checked ? setSelectedClients(filteredClients.map(c => c.id)) : setSelectedClients([]); }}
                      className="rounded border-gray-300" />
                  </th>
                  <th className="text-left p-3 font-semibold text-gray-700">Casillero</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Nombre</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Telefono</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Tipo</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Registrado</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map(client => (
                  <tr key={client.id} className={`border-b hover:bg-gray-50 ${selectedClients.includes(client.id) ? 'bg-blue-50' : ''}`}>
                    <td className="p-3">
                      <input type="checkbox" checked={selectedClients.includes(client.id)}
                        onChange={() => setSelectedClients(prev => prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id])}
                        className="rounded border-gray-300" />
                    </td>
                    <td className="p-3">
                      <span className="inline-block bg-green-500 text-white px-2 py-1 rounded text-xs font-mono">{client.locker_number || 'N/A'}</span>
                    </td>
                    <td className="p-3 font-semibold">{client.full_name}</td>
                    <td className="p-3 text-gray-600">{client.email}</td>
                    <td className="p-3 text-gray-600">{client.phone_country_code && client.phone ? `${client.phone_country_code} ${client.phone}` : 'N/A'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${client.customer_type === 'alumno' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                        {client.customer_type === 'alumno' ? 'Alumno' : 'Publico'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600 text-xs">{new Date(client.created_at).toLocaleDateString()}</td>
                    <td className="p-3 flex gap-1 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => openEditClientModal(client)} data-testid={`edit-client-${client.id}`}>Editar</Button>
                      <Button variant="ghost" size="sm" className="text-blue-600 text-xs" onClick={() => onNavigate && onNavigate('warehouse', { filterClientId: client.id })}>WRs</Button>
                      <Button variant="ghost" size="sm" className="text-green-600 text-xs" onClick={() => onNavigate && onNavigate('invoices', { filterClientId: client.id })}>Facturas</Button>
                      <Button variant="ghost" size="sm" className="text-purple-600 text-xs" onClick={() => onNavigate && onNavigate('account-statement', { clientId: client.id })}>Estado Cta</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredClients.length === 0 && <p className="text-center text-gray-500 py-8">No hay clientes registrados</p>}
        </CardContent>
      </Card>

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar Cliente</h2>
              <button onClick={() => setEditingClient(null)} className="text-gray-500 hover:text-gray-700 text-2xl">x</button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Completo</Label>
                <Input value={editClientForm.full_name} onChange={(e) => setEditClientForm({...editClientForm, full_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editClientForm.email} onChange={(e) => setEditClientForm({...editClientForm, email: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Codigo Pais</Label>
                <Select value={editClientForm.phone_country_code} onValueChange={(v) => setEditClientForm({...editClientForm, phone_country_code: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+58">+58 VE</SelectItem>
                    <SelectItem value="+1">+1 US</SelectItem>
                    <SelectItem value="+57">+57 CO</SelectItem>
                    <SelectItem value="+34">+34 ES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={editClientForm.phone} onChange={(e) => setEditClientForm({...editClientForm, phone: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Documento ID / Cedula</Label>
                <Input value={editClientForm.id_document} onChange={(e) => setEditClientForm({...editClientForm, id_document: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Tipo de Cliente</Label>
                <Select value={editClientForm.customer_type} onValueChange={(v) => setEditClientForm({...editClientForm, customer_type: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publico">Publico</SelectItem>
                    <SelectItem value="alumno">Alumnos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Direccion de Entrega</Label>
                <Input value={editClientForm.delivery_address} onChange={(e) => setEditClientForm({...editClientForm, delivery_address: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Ciudad de Entrega</Label>
                <Input value={editClientForm.delivery_city} onChange={(e) => setEditClientForm({...editClientForm, delivery_city: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Estado de Entrega</Label>
                <Input value={editClientForm.delivery_state} onChange={(e) => setEditClientForm({...editClientForm, delivery_state: e.target.value})} className="mt-1" />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editClientForm.is_active} onChange={(e) => setEditClientForm({...editClientForm, is_active: e.target.checked})} className="rounded border-gray-300" />
                  <span>Cliente Activo</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setEditingClient(null)}>Cancelar</Button>
              <Button onClick={handleSaveClientEdit}>Guardar Cambios</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminClients;
